import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Mic, Video, RefreshCw, ChevronDown, ChevronUp, Mail, Phone, Building2, Globe, Calendar, Lock, Plus, Trash2, Edit2, Tag, Image, Settings, X, Check, Save, List, PlayCircle, Upload, Loader2, BookOpen, Heart, GripVertical, Shield, KeyRound } from 'lucide-react';
import { toast } from 'sonner';
import SEO from '../components/SEO';
import SecurityTab from './admin/SecurityTab';
import ApiAccessTab from './admin/ApiAccessTab';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Service slugs for the service images management
const SERVICE_SLUGS = [
  { slug: 'film-production', name: 'Film Production' },
  { slug: 'wedding-cinematography', name: 'Wedding Cinematography' },
  { slug: 'real-estate-media', name: 'Real Estate Media' },
  { slug: 'event-broadcasting', name: 'Event Broadcasting' },
  { slug: 'website-development', name: 'Website Development' },
  { slug: 'seo-strategies', name: 'SEO Strategies' },
  { slug: 'digital-advertising', name: 'Digital Advertising' },
  { slug: 'ai-automations', name: 'AI Automations' },
  { slug: 'brand-identity', name: 'Brand Identity' },
  { slug: 'content-creation', name: 'Content Creation' },
  { slug: 'graphic-design', name: 'Graphic Design' },
  { slug: 'motion-graphics', name: 'Motion Graphics' },
  { slug: 'media-strategy', name: 'Media Strategy' },
  { slug: 'technology-advisory', name: 'Technology Advisory' },
  { slug: 'budget-appraisals', name: 'Budget & Appraisals' },
  { slug: 'automation-consulting', name: 'Automation Consulting' },
  { slug: 'content-strategy', name: 'Content Strategy' },
  { slug: 'audience-audit', name: 'Audience Audit' }
];

const AdminPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authToken, setAuthToken] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [totpRequired, setTotpRequired] = useState(false);
  const [totpCode, setTotpCode] = useState('');
  const [useRecovery, setUseRecovery] = useState(false);
  const [activeTab, setActiveTab] = useState('inquiries');
  const [podcastApps, setPodcastApps] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [videos, setVideos] = useState([]);
  const [tags, setTags] = useState([]);
  const [siteSettings, setSiteSettings] = useState({ about_portrait_url: '' });
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  
  // Podcast Series states
  const [podcastSeries, setPodcastSeries] = useState([]);
  const [showSeriesForm, setShowSeriesForm] = useState(false);
  const [editingSeries, setEditingSeries] = useState(null);
  const [seriesForm, setSeriesForm] = useState({ title: '', description: '', thumbnail_url: '' });
  const [expandedSeriesId, setExpandedSeriesId] = useState(null);
  const [showEpisodeForm, setShowEpisodeForm] = useState(false);
  const [editingEpisode, setEditingEpisode] = useState(null);
  const [episodeForm, setEpisodeForm] = useState({ title: '', dropbox_url: '', thumbnail_url: '' });
  const [activeSeriesForEpisode, setActiveSeriesForEpisode] = useState(null);
  
  // Form states
  const [showVideoForm, setShowVideoForm] = useState(false);
  const [showTagForm, setShowTagForm] = useState(false);
  const [editingVideo, setEditingVideo] = useState(null);
  const [videoForm, setVideoForm] = useState({ title: '', video_url: '', thumbnail_url: '', tags: [] });
  const [newTag, setNewTag] = useState('');
  
  // Upload states
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  const videoInputRef = useRef(null);
  const episodeVideoInputRef = useRef(null);
  const [episodeUploading, setEpisodeUploading] = useState(false);
  
  // Service Images states
  const [serviceImages, setServiceImages] = useState({});
  const [uploadingService, setUploadingService] = useState(null);
  const serviceImageRefs = useRef({});
  
  // Stories states
  const [stories, setStories] = useState([]);
  const [expandedStorySlug, setExpandedStorySlug] = useState(null);
  const [storyUploading, setStoryUploading] = useState(null);
  const [showStoryForm, setShowStoryForm] = useState(false);
  const [editingStory, setEditingStory] = useState(null);
  const [storyForm, setStoryForm] = useState({
    slug: '',
    client_name: '',
    title: '',
    hero_image: '',
    hero_video: '',
    content: '',
    content_blocks: [],  // NEW: Rich content blocks
    gallery_images: [],
    videos: [],
    podcast_link: '',
    services_provided: []
  });
  const storyHeroImageRef = useRef(null);
  const storyHeroVideoRef = useRef(null);
  const storyGalleryRef = useRef(null);
  const storyVideoRef = useRef(null);
  const blockMediaRef = useRef(null);

  // Wedding Films states
  const [weddingFilms, setWeddingFilms] = useState([]);
  const [showWeddingForm, setShowWeddingForm] = useState(false);
  const [editingWedding, setEditingWedding] = useState(null);
  const [weddingForm, setWeddingForm] = useState({
    title: '',
    couple_names: '',
    youtube_url: '',
    thumbnail_url: '',
    location: '',
    wedding_date: '',
    featured: false
  });
  const weddingThumbnailRef = useRef(null);

  // Check stored token on mount
  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (token) {
      verifyToken(token);
    }
  }, []);

  const verifyToken = async (token) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/verify`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setAuthToken(token);
        setIsAuthenticated(true);
      } else {
        localStorage.removeItem('admin_token');
      }
    } catch (e) {
      localStorage.removeItem('admin_token');
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    
    try {
      const body = { password };
      if (totpRequired && totpCode.trim()) {
        if (useRecovery) body.recovery_code = totpCode.trim();
        else body.totp_code = totpCode.trim();
      }
      const res = await fetch(`${API_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      
      if (!res.ok) {
        const data = await res.json();
        if (data.detail === 'totp_required') {
          setTotpRequired(true);
          setError('');
          return;
        }
        setError(data.detail || 'Invalid password');
        return;
      }
      
      const data = await res.json();
      setAuthToken(data.token);
      localStorage.setItem('admin_token', data.token);
      setIsAuthenticated(true);
      setPassword('');
      setTotpRequired(false);
      setTotpCode('');
      setUseRecovery(false);
    } catch (e) {
      setError('Login failed. Please try again.');
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API_URL}/api/admin/logout`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
    } catch (e) {
      // Ignore logout errors
    }
    
    localStorage.removeItem('admin_token');
    setAuthToken('');
    setIsAuthenticated(false);
  };

  // Fetch data with current token
  const fetchData = useCallback(async () => {
    if (!authToken) return;
    setLoading(true);
    
    const headers = { 'Authorization': `Bearer ${authToken}` };
    
    try {
      const [podcastRes, videosRes, tagsRes, settingsRes, seriesRes, storiesRes, serviceImagesRes, weddingRes, inquiriesRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/podcast-applications`, { headers }),
        fetch(`${API_URL}/api/videos`),
        fetch(`${API_URL}/api/tags`),
        fetch(`${API_URL}/api/site-settings`),
        fetch(`${API_URL}/api/podcast-series`),
        fetch(`${API_URL}/api/stories`),
        fetch(`${API_URL}/api/service-images`),
        fetch(`${API_URL}/api/wedding-films`),
        fetch(`${API_URL}/api/admin/inquiries`, { headers })
      ]);
      
      if (podcastRes.ok) setPodcastApps((await podcastRes.json()).reverse());
      if (inquiriesRes.ok) setInquiries(await inquiriesRes.json());
      if (videosRes.ok) setVideos(await videosRes.json());
      if (tagsRes.ok) setTags(await tagsRes.json());
      if (settingsRes.ok) setSiteSettings(await settingsRes.json());
      if (seriesRes.ok) setPodcastSeries(await seriesRes.json());
      if (storiesRes.ok) setStories(await storiesRes.json());
      if (serviceImagesRes.ok) setServiceImages(await serviceImagesRes.json());
      if (weddingRes.ok) setWeddingFilms(await weddingRes.json());
    } catch (e) {
      console.error('Error fetching data:', e);
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  }, [authToken]);

  useEffect(() => {
    if (isAuthenticated && authToken) {
      fetchData();
    }
  }, [isAuthenticated, authToken, fetchData]);

  // Video Upload Handler
  const handleVideoUpload = async (file) => {
    if (!file) return;
    
    setUploading(true);
    setUploadProgress('Uploading video...');
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const res = await fetch(`${API_URL}/api/admin/upload/video`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${authToken}` },
        body: formData
      });
      
      if (res.ok) {
        const data = await res.json();
        setVideoForm(prev => ({
          ...prev,
          video_url: data.video_url,
          thumbnail_url: data.thumbnail_url
        }));
        toast.success('Video uploaded successfully!');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Upload failed');
      }
    } catch (e) {
      console.error('Upload error:', e);
      toast.error('Upload failed');
    } finally {
      setUploading(false);
      setUploadProgress('');
    }
  };

  // Service Image Upload Handler
  const handleServiceImageUpload = async (slug, file) => {
    if (!file) return;
    
    setUploadingService(slug);
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const res = await fetch(`${API_URL}/api/admin/service-images/upload/${slug}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${authToken}` },
        body: formData
      });
      
      if (res.ok) {
        const data = await res.json();
        setServiceImages(prev => ({ ...prev, [slug]: data.hero_url }));
        toast.success('Image uploaded!');
      } else {
        toast.error('Upload failed');
      }
    } catch (e) {
      toast.error('Upload failed');
    } finally {
      setUploadingService(null);
    }
  };

  // Video CRUD
  const handleSaveVideo = async () => {
    if (!videoForm.title || (!videoForm.video_url)) {
      toast.error('Title and video are required');
      return;
    }
    
    const headers = { 
      'Authorization': `Bearer ${authToken}`,
      'Content-Type': 'application/json'
    };
    
    try {
      let res;
      if (editingVideo) {
        // Update existing video
        res = await fetch(`${API_URL}/api/admin/videos/${editingVideo.id}`, {
          method: 'PUT',
          headers,
          body: JSON.stringify({
            title: videoForm.title,
            video_url: videoForm.video_url,
            thumbnail_url: videoForm.thumbnail_url,
            tags: videoForm.tags
          })
        });
      } else {
        // Create new video
        res = await fetch(`${API_URL}/api/admin/videos`, {
          method: 'POST',
          headers,
          body: JSON.stringify(videoForm)
        });
      }
      
      if (res.ok) {
        toast.success(editingVideo ? 'Video updated' : 'Video added');
        setVideoForm({ title: '', video_url: '', thumbnail_url: '', tags: [] });
        setShowVideoForm(false);
        setEditingVideo(null);
        fetchData();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to save video');
      }
    } catch (e) {
      console.error('Error saving video:', e);
      toast.error('Error saving video');
    }
  };

  const handleEditVideo = (video) => {
    setEditingVideo(video);
    setVideoForm({
      title: video.title,
      video_url: video.video_url || video.streaming_url || '',
      thumbnail_url: video.thumbnail_url || '',
      tags: video.tags || []
    });
    setShowVideoForm(true);
  };

  const handleCancelVideoForm = () => {
    setShowVideoForm(false);
    setEditingVideo(null);
    setVideoForm({ title: '', video_url: '', thumbnail_url: '', tags: [] });
  };

  const handleDeleteVideo = async (videoId) => {
    if (!window.confirm('Delete this video?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/admin/videos/${videoId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      
      if (res.ok) {
        toast.success('Video deleted');
        fetchData();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to delete video');
      }
    } catch (e) {
      console.error('Error deleting video:', e);
      toast.error('Error deleting video');
    }
  };

  // Tag CRUD
  const handleAddTag = async () => {
    if (!newTag.trim()) return;
    
    try {
      const res = await fetch(`${API_URL}/api/admin/tags`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ name: newTag })
      });
      
      if (res.ok) {
        toast.success('Tag added');
        setNewTag('');
        setShowTagForm(false);
        fetchData();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to add tag');
      }
    } catch (e) {
      toast.error('Error adding tag');
    }
  };

  const handleDeleteTag = async (tagId) => {
    if (!window.confirm('Delete this tag? It will be removed from all videos.')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/admin/tags/${tagId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      
      if (res.ok) {
        toast.success('Tag deleted');
        fetchData();
      } else {
        toast.error('Failed to delete tag');
      }
    } catch (e) {
      toast.error('Error deleting tag');
    }
  };

  // Podcast Series CRUD
  const handleSaveSeries = async () => {
    if (!seriesForm.title) {
      toast.error('Series title is required');
      return;
    }
    
    const headers = { 
      'Authorization': `Bearer ${authToken}`,
      'Content-Type': 'application/json'
    };
    
    try {
      let res;
      if (editingSeries) {
        res = await fetch(`${API_URL}/api/admin/podcast-series/${editingSeries.id}`, {
          method: 'PUT',
          headers,
          body: JSON.stringify(seriesForm)
        });
      } else {
        res = await fetch(`${API_URL}/api/admin/podcast-series`, {
          method: 'POST',
          headers,
          body: JSON.stringify(seriesForm)
        });
      }
      
      if (res.ok) {
        toast.success(editingSeries ? 'Series updated' : 'Series added');
        setSeriesForm({ title: '', description: '', thumbnail_url: '' });
        setShowSeriesForm(false);
        setEditingSeries(null);
        fetchData();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to save series');
      }
    } catch (e) {
      console.error('Error saving series:', e);
      toast.error('Error saving series');
    }
  };

  const handleEditSeries = (series) => {
    setEditingSeries(series);
    setSeriesForm({
      title: series.title,
      description: series.description || '',
      thumbnail_url: series.thumbnail_url || ''
    });
    setShowSeriesForm(true);
  };

  const handleDeleteSeries = async (seriesId) => {
    if (!window.confirm('Delete this series and all its episodes?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/admin/podcast-series/${seriesId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      
      if (res.ok) {
        toast.success('Series deleted');
        fetchData();
      } else {
        toast.error('Failed to delete series');
      }
    } catch (e) {
      toast.error('Error deleting series');
    }
  };

  const handleCancelSeriesForm = () => {
    setShowSeriesForm(false);
    setEditingSeries(null);
    setSeriesForm({ title: '', description: '', thumbnail_url: '' });
  };

  // Episode Video Upload Handler
  const handleEpisodeVideoUpload = async (file) => {
    if (!file) return;
    
    setEpisodeUploading(true);
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const res = await fetch(`${API_URL}/api/admin/upload/video`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${authToken}` },
        body: formData
      });
      
      if (res.ok) {
        const data = await res.json();
        setEpisodeForm(prev => ({
          ...prev,
          video_url: data.video_url,
          thumbnail_url: data.thumbnail_url
        }));
        toast.success('Video uploaded!');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Upload failed');
      }
    } catch (e) {
      console.error('Upload error:', e);
      toast.error('Upload failed');
    } finally {
      setEpisodeUploading(false);
    }
  };

  // Podcast Episode CRUD
  const handleSaveEpisode = async () => {
    if (!episodeForm.title || !episodeForm.video_url) {
      toast.error('Title and video are required');
      return;
    }
    
    const headers = { 
      'Authorization': `Bearer ${authToken}`,
      'Content-Type': 'application/json'
    };
    
    try {
      let res;
      if (editingEpisode) {
        res = await fetch(`${API_URL}/api/admin/podcast-episodes/${editingEpisode.id}`, {
          method: 'PUT',
          headers,
          body: JSON.stringify({
            title: episodeForm.title,
            video_url: episodeForm.video_url,
            thumbnail_url: episodeForm.thumbnail_url
          })
        });
      } else {
        res = await fetch(`${API_URL}/api/admin/podcast-episodes`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            title: episodeForm.title,
            video_url: episodeForm.video_url,
            thumbnail_url: episodeForm.thumbnail_url,
            series_id: activeSeriesForEpisode
          })
        });
      }
      
      if (res.ok) {
        toast.success(editingEpisode ? 'Episode updated' : 'Episode added');
        setEpisodeForm({ title: '', video_url: '', thumbnail_url: '' });
        setShowEpisodeForm(false);
        setEditingEpisode(null);
        fetchData();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to save episode');
      }
    } catch (e) {
      console.error('Error saving episode:', e);
      toast.error('Error saving episode');
    }
  };

  const handleEditEpisode = (episode) => {
    setEditingEpisode(episode);
    setEpisodeForm({
      title: episode.title,
      video_url: episode.video_url || episode.streaming_url || '',
      thumbnail_url: episode.thumbnail_url || ''
    });
    setShowEpisodeForm(true);
  };

  const handleDeleteEpisode = async (episodeId) => {
    if (!window.confirm('Delete this episode?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/admin/podcast-episodes/${episodeId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      
      if (res.ok) {
        toast.success('Episode deleted');
        fetchData();
      } else {
        toast.error('Failed to delete episode');
      }
    } catch (e) {
      toast.error('Error deleting episode');
    }
  };

  const handleCancelEpisodeForm = () => {
    setShowEpisodeForm(false);
    setEditingEpisode(null);
    setEpisodeForm({ title: '', video_url: '', thumbnail_url: '' });
  };

  const openAddEpisode = (seriesId) => {
    setActiveSeriesForEpisode(seriesId);
    setEditingEpisode(null);
    setEpisodeForm({ title: '', video_url: '', thumbnail_url: '' });
    setShowEpisodeForm(true);
  };

  // Stories CRUD Handlers
  const handleStoryHeroImageUpload = async (file) => {
    if (!file) return;
    setStoryUploading('hero_image');
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const res = await fetch(`${API_URL}/api/admin/upload/image`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${authToken}` },
        body: formData
      });
      
      if (res.ok) {
        const data = await res.json();
        setStoryForm(prev => ({ ...prev, hero_image: data.image_url }));
        toast.success('Hero image uploaded!');
      } else {
        const error = await res.json().catch(() => ({ detail: 'Upload failed' }));
        toast.error(error.detail || 'Upload failed');
        console.error('Upload error:', error);
      }
    } catch (e) {
      console.error('Upload error:', e);
      toast.error('Upload failed: ' + e.message);
    } finally {
      setStoryUploading(null);
    }
  };

  const handleStoryHeroVideoUpload = async (file) => {
    if (!file) return;
    setStoryUploading('hero_video');
    toast.info('Uploading video... This may take a moment.');
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const res = await fetch(`${API_URL}/api/admin/upload/video`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${authToken}` },
        body: formData
      });
      
      if (res.ok) {
        const data = await res.json();
        setStoryForm(prev => ({ ...prev, hero_video: data.video_url }));
        toast.success('Hero video uploaded!');
      } else {
        const error = await res.json().catch(() => ({ detail: 'Upload failed' }));
        toast.error(error.detail || 'Upload failed');
        console.error('Upload error:', error);
      }
    } catch (e) {
      console.error('Upload error:', e);
      toast.error('Upload failed: ' + e.message);
    } finally {
      setStoryUploading(null);
    }
  };

  const handleStoryGalleryImageUpload = async (file) => {
    if (!file) return;
    setStoryUploading('gallery');
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const res = await fetch(`${API_URL}/api/admin/upload/image`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${authToken}` },
        body: formData
      });
      
      if (res.ok) {
        const data = await res.json();
        setStoryForm(prev => ({
          ...prev,
          gallery_images: [...prev.gallery_images, { url: data.image_url, caption: '' }]
        }));
        toast.success('Gallery image added!');
      } else {
        const error = await res.json().catch(() => ({ detail: 'Upload failed' }));
        toast.error(error.detail || 'Upload failed');
        console.error('Upload error:', error);
      }
    } catch (e) {
      console.error('Upload error:', e);
      toast.error('Upload failed: ' + e.message);
    } finally {
      setStoryUploading(null);
    }
  };

  const handleStoryVideoUpload = async (file) => {
    if (!file) return;
    setStoryUploading('video');
    toast.info('Uploading video... This may take a moment.');
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const res = await fetch(`${API_URL}/api/admin/upload/video`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${authToken}` },
        body: formData
      });
      
      if (res.ok) {
        const data = await res.json();
        setStoryForm(prev => ({
          ...prev,
          videos: [...prev.videos, { url: data.video_url, title: '', thumbnail: data.thumbnail_url }]
        }));
        toast.success('Video added!');
      } else {
        const error = await res.json().catch(() => ({ detail: 'Upload failed' }));
        toast.error(error.detail || 'Upload failed');
        console.error('Upload error:', error);
      }
    } catch (e) {
      console.error('Upload error:', e);
      toast.error('Upload failed: ' + e.message);
    } finally {
      setStoryUploading(null);
    }
  };

  const handleRemoveGalleryImage = (index) => {
    setStoryForm(prev => ({
      ...prev,
      gallery_images: prev.gallery_images.filter((_, i) => i !== index)
    }));
  };

  const handleRemoveStoryVideo = (index) => {
    setStoryForm(prev => ({
      ...prev,
      videos: prev.videos.filter((_, i) => i !== index)
    }));
  };

  const handleUpdateGalleryCaption = (index, caption) => {
    setStoryForm(prev => ({
      ...prev,
      gallery_images: prev.gallery_images.map((img, i) => 
        i === index ? { ...img, caption } : img
      )
    }));
  };

  const handleUpdateVideoTitle = (index, title) => {
    setStoryForm(prev => ({
      ...prev,
      videos: prev.videos.map((vid, i) => 
        i === index ? { ...vid, title } : vid
      )
    }));
  };

  const slugify = (text) => {
    return text.toLowerCase().trim().replace(/[^\w\s-]/g, '').replace(/[\s_-]+/g, '-');
  };

  const handleSaveStory = async () => {
    if (!storyForm.client_name || !storyForm.title) {
      toast.error('Client name and title are required');
      return;
    }
    
    const headers = { 
      'Authorization': `Bearer ${authToken}`,
      'Content-Type': 'application/json'
    };
    
    try {
      let res;
      const storyData = {
        ...storyForm,
        slug: storyForm.slug || slugify(storyForm.client_name)
      };
      
      if (editingStory) {
        res = await fetch(`${API_URL}/api/admin/stories/${editingStory.slug}`, {
          method: 'PUT',
          headers,
          body: JSON.stringify(storyData)
        });
      } else {
        res = await fetch(`${API_URL}/api/admin/stories`, {
          method: 'POST',
          headers,
          body: JSON.stringify(storyData)
        });
      }
      
      if (res.ok) {
        toast.success(editingStory ? 'Story updated' : 'Story created');
        handleCancelStoryForm();
        fetchData();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to save story');
      }
    } catch (e) {
      console.error('Error saving story:', e);
      toast.error('Error saving story');
    }
  };

  const handleEditStory = (story) => {
    setEditingStory(story);
    setStoryForm({
      slug: story.slug || '',
      client_name: story.client_name || '',
      title: story.title || '',
      hero_image: story.hero_image || '',
      hero_video: story.hero_video || '',
      content: story.content || '',
      content_blocks: story.content_blocks || [],
      gallery_images: story.gallery_images || [],
      videos: story.videos || [],
      podcast_link: story.podcast_link || '',
      services_provided: story.services_provided || []
    });
    setShowStoryForm(true);
  };

  const handleDeleteStory = async (slug) => {
    if (!window.confirm('Delete this story? This cannot be undone.')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/admin/stories/${slug}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      
      if (res.ok) {
        toast.success('Story deleted');
        fetchData();
      } else {
        toast.error('Failed to delete story');
      }
    } catch (e) {
      toast.error('Error deleting story');
    }
  };

  const handleCancelStoryForm = () => {
    setShowStoryForm(false);
    setEditingStory(null);
    setStoryForm({
      slug: '',
      client_name: '',
      title: '',
      hero_image: '',
      hero_video: '',
      content: '',
      content_blocks: [],
      gallery_images: [],
      videos: [],
      podcast_link: '',
      services_provided: []
    });
  };

  // Content Block Handlers
  const addContentBlock = (type) => {
    const newBlock = {
      id: Date.now().toString(),
      type,
      content: '',
      caption: '',
      order: storyForm.content_blocks.length
    };
    setStoryForm(prev => ({
      ...prev,
      content_blocks: [...prev.content_blocks, newBlock]
    }));
  };

  const updateContentBlock = (id, field, value) => {
    setStoryForm(prev => ({
      ...prev,
      content_blocks: prev.content_blocks.map(block =>
        block.id === id ? { ...block, [field]: value } : block
      )
    }));
  };

  const deleteContentBlock = (id) => {
    setStoryForm(prev => ({
      ...prev,
      content_blocks: prev.content_blocks.filter(block => block.id !== id)
    }));
  };

  const moveContentBlock = (id, direction) => {
    const blocks = [...storyForm.content_blocks];
    const index = blocks.findIndex(b => b.id === id);
    if (direction === 'up' && index > 0) {
      [blocks[index], blocks[index - 1]] = [blocks[index - 1], blocks[index]];
    } else if (direction === 'down' && index < blocks.length - 1) {
      [blocks[index], blocks[index + 1]] = [blocks[index + 1], blocks[index]];
    }
    // Update order values
    blocks.forEach((block, idx) => block.order = idx);
    setStoryForm(prev => ({ ...prev, content_blocks: blocks }));
  };

  const handleBlockMediaUpload = async (blockId, file, type) => {
    if (!file) return;
    setStoryUploading(`block_${blockId}`);
    
    const formData = new FormData();
    formData.append('file', file);
    
    const endpoint = type === 'video' ? 'video' : 'image';
    
    try {
      const res = await fetch(`${API_URL}/api/admin/upload/${endpoint}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${authToken}` },
        body: formData
      });
      
      if (res.ok) {
        const data = await res.json();
        const url = type === 'video' ? data.video_url : data.image_url;
        updateContentBlock(blockId, 'content', url);
        toast.success(`${type === 'video' ? 'Video' : 'Media'} uploaded!`);
      } else {
        const error = await res.json().catch(() => ({ detail: 'Upload failed' }));
        toast.error(error.detail || 'Upload failed');
      }
    } catch (e) {
      toast.error('Upload failed: ' + e.message);
    } finally {
      setStoryUploading(null);
    }
  };

  const handleAddService = (service) => {
    if (service && !storyForm.services_provided.includes(service)) {
      setStoryForm(prev => ({
        ...prev,
        services_provided: [...prev.services_provided, service]
      }));
    }
  };

  const handleRemoveService = (service) => {
    setStoryForm(prev => ({
      ...prev,
      services_provided: prev.services_provided.filter(s => s !== service)
    }));
  };

  // Site Settings
  const handleUpdateSettings = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/site-settings`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(siteSettings)
      });
      
      if (res.ok) {
        toast.success('Settings saved');
      } else {
        toast.error('Failed to save settings');
      }
    } catch (e) {
      toast.error('Error saving settings');
    }
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#0a0a0a]">
        <SEO title="Admin Login" canonical="https://creativedirectorkhan.com/admin" />
        
        <section className="min-h-screen flex items-center justify-center px-8">
          <div className="w-full max-w-md">
            <div className="text-center mb-10">
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-white/5 border border-white/10 flex items-center justify-center">
                <Lock className="w-8 h-8 text-white/60" />
              </div>
              <h1 className="font-serif text-4xl font-light text-white mb-2">Admin Portal</h1>
              <p className="text-white/50">Enter password to continue</p>
            </div>
            
            <form onSubmit={handleLogin} className="space-y-6">
              <div>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password"
                  disabled={totpRequired}
                  className="w-full bg-white/[0.02] border border-white/10 px-6 py-4 text-white text-base placeholder:text-white/30 focus:border-white/30 focus:outline-none disabled:opacity-50"
                  autoFocus
                  data-testid="admin-password-input"
                />
              </div>

              {totpRequired && (
                <div data-testid="admin-totp-step">
                  <label className="text-white/50 text-xs uppercase tracking-wider mb-2 block">
                    {useRecovery ? 'Recovery code' : 'Authenticator code'}
                  </label>
                  <input
                    type="text"
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value)}
                    placeholder={useRecovery ? 'Recovery code' : '123456'}
                    className="w-full bg-white/[0.02] border border-white/10 px-6 py-4 text-white text-base tracking-[0.3em] placeholder:text-white/30 focus:border-white/30 focus:outline-none"
                    autoFocus
                    data-testid="admin-totp-input"
                  />
                  <button
                    type="button"
                    onClick={() => { setUseRecovery(!useRecovery); setTotpCode(''); }}
                    className="text-white/40 text-xs hover:text-white/70 mt-2 underline"
                    data-testid="admin-toggle-recovery"
                  >
                    {useRecovery ? 'Use authenticator app instead' : "Can't access your app? Use a recovery code"}
                  </button>
                </div>
              )}

              {error && <p className="text-red-400 text-sm" data-testid="admin-login-error">{error}</p>}
              
              <button type="submit" className="w-full px-8 py-4 bg-white text-black font-medium hover:bg-white/90 transition-all" data-testid="admin-login-submit">
                {totpRequired ? 'Verify & Sign In' : 'Access Portal'}
              </button>
            </form>
          </div>
        </section>
      </div>
    );
  }

  // Helper function for formatting date
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  // Inline application card rendering
  const renderApplicationCard = (app) => {
    const isExpanded = expandedId === app.id;
    return (
      <div key={app.id} className="border border-white/10 bg-white/[0.02]">
        <button onClick={() => setExpandedId(isExpanded ? null : app.id)} className="w-full p-5 flex items-center justify-between text-left">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center">
              <span className="font-serif text-base text-white/70">{app.name?.charAt(0)?.toUpperCase()}</span>
            </div>
            <div>
              <h3 className="text-white font-medium text-sm">{app.name}</h3>
              <p className="text-white/50 text-xs">{app.business_name}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-white/40 text-xs hidden md:block">{formatDate(app.submitted_at)}</span>
            {isExpanded ? <ChevronUp className="w-4 h-4 text-white/40" /> : <ChevronDown className="w-4 h-4 text-white/40" />}
          </div>
        </button>
        
        {isExpanded && (
          <div className="px-5 pb-5 border-t border-white/5 pt-5 space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <a href={`mailto:${app.email}`} className="flex items-center gap-2 text-white/60 hover:text-white"><Mail className="w-4 h-4" /><span className="truncate">{app.email}</span></a>
              <a href={`tel:${app.contact_number}`} className="flex items-center gap-2 text-white/60 hover:text-white"><Phone className="w-4 h-4" />{app.contact_number}</a>
              <div className="flex items-center gap-2 text-white/60"><Building2 className="w-4 h-4" />{app.years_in_business}</div>
              <div className="flex items-center gap-2 text-white/60"><Globe className="w-4 h-4" />{app.country}</div>
            </div>
            {app.social_media && <div><p className="text-white/40 text-xs uppercase mb-1">Social</p><p className="text-white/70 text-sm">{app.social_media}</p></div>}
            <div><p className="text-white/40 text-xs uppercase mb-1">Story</p><p className="text-white/70 text-sm whitespace-pre-wrap">{app.your_story}</p></div>
            <div><p className="text-white/40 text-xs uppercase mb-1">Unique</p><p className="text-white/70 text-sm whitespace-pre-wrap">{app.what_makes_unique}</p></div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <SEO title="Admin Portal" canonical="https://creativedirectorkhan.com/admin" />

      {/* Header */}
      <section className="pt-32 pb-6 px-6 md:px-12 border-b border-white/5">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <p className="text-white/40 text-xs tracking-[0.3em] uppercase mb-1">Admin Portal</p>
            <h1 className="font-serif text-3xl font-light text-white">Dashboard</h1>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={fetchData} disabled={loading} className="flex items-center gap-2 px-4 py-2 border border-white/20 text-white/70 hover:text-white text-sm">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />Refresh
            </button>
            <button onClick={handleLogout} className="px-4 py-2 border border-white/10 text-white/50 hover:text-white text-sm">Logout</button>
          </div>
        </div>
      </section>

      {/* Main Tabs */}
      <section className="px-6 md:px-12 py-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex gap-1 mb-6 border-b border-white/10 overflow-x-auto">
            {[
              { id: 'inquiries', label: 'Inquiries', icon: Mail },
              { id: 'applications', label: 'Applications', icon: List },
              { id: 'podcastSeries', label: 'Podcast Series', icon: Mic },
              { id: 'stories', label: 'Stories', icon: BookOpen },
              { id: 'weddingFilms', label: 'Wedding Films', icon: Heart },
              { id: 'videos', label: 'Videos', icon: Video },
              { id: 'tags', label: 'Tags', icon: Tag },
              { id: 'serviceImages', label: 'Service Images', icon: Image },
              { id: 'apiAccess', label: 'API Access', icon: KeyRound },
              { id: 'security', label: 'Security', icon: Shield },
              { id: 'settings', label: 'Settings', icon: Settings }
            ].map(tab => (
              <button
                key={tab.id}
                data-testid={`tab-${tab.id}`}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm transition-all border-b-2 -mb-px whitespace-nowrap ${
                  activeTab === tab.id ? 'border-white text-white' : 'border-transparent text-white/50 hover:text-white'
                }`}
              >
                <tab.icon className="w-4 h-4" />{tab.label}
              </button>
            ))}
          </div>

          {/* Inquiries Tab */}
          {activeTab === 'inquiries' && (
            <div>
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-white text-lg">
                  Inquiries <span className="text-white/40 text-sm ml-2">{inquiries.length} total</span>
                  {inquiries.filter(i => !i.read).length > 0 && (
                    <span className="ml-2 px-2 py-0.5 bg-white text-black text-xs font-medium">{inquiries.filter(i => !i.read).length} new</span>
                  )}
                </h2>
              </div>

              {inquiries.length === 0 ? (
                <div className="text-center py-16 border border-white/10">
                  <Mail className="w-8 h-8 text-white/20 mx-auto mb-3" />
                  <p className="text-white/40">No inquiries yet</p>
                  <p className="text-white/30 text-sm mt-1">Inquiries from the website Inquire button will appear here</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {inquiries.map(inq => (
                    <div 
                      key={inq.id} 
                      data-testid={`inquiry-card-${inq.id}`}
                      className={`border p-5 transition-all ${inq.read ? 'border-white/5 bg-transparent' : 'border-white/20 bg-white/[0.03]'}`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3 mb-2">
                            {!inq.read && <span className="w-2 h-2 bg-white rounded-full flex-shrink-0" />}
                            <span className="text-white font-medium">{inq.name}</span>
                            <a href={`mailto:${inq.email}`} className="text-white/50 text-sm hover:text-white transition-colors">{inq.email}</a>
                          </div>
                          <p className="text-white/70 text-sm leading-relaxed">{inq.message}</p>
                          <p className="text-white/30 text-xs mt-3">
                            {inq.submitted_at ? new Date(inq.submitted_at).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : ''}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <button
                            data-testid={`inquiry-read-${inq.id}`}
                            onClick={async () => {
                              try {
                                const res = await fetch(`${API_URL}/api/admin/inquiries/${inq.id}/read`, {
                                  method: 'PUT',
                                  headers: { 'Authorization': `Bearer ${authToken}` }
                                });
                                if (res.ok) {
                                  setInquiries(prev => prev.map(i => i.id === inq.id ? { ...i, read: !i.read } : i));
                                }
                              } catch (e) { toast.error('Failed to update'); }
                            }}
                            className={`px-3 py-1.5 text-xs border transition-all ${inq.read ? 'border-white/10 text-white/40 hover:text-white' : 'border-white/20 text-white hover:bg-white/10'}`}
                          >
                            {inq.read ? 'Mark Unread' : 'Mark Read'}
                          </button>
                          <button
                            data-testid={`inquiry-delete-${inq.id}`}
                            onClick={async () => {
                              if (!window.confirm('Delete this inquiry?')) return;
                              try {
                                const res = await fetch(`${API_URL}/api/admin/inquiries/${inq.id}`, {
                                  method: 'DELETE',
                                  headers: { 'Authorization': `Bearer ${authToken}` }
                                });
                                if (res.ok) {
                                  setInquiries(prev => prev.filter(i => i.id !== inq.id));
                                  toast.success('Inquiry deleted');
                                }
                              } catch (e) { toast.error('Failed to delete'); }
                            }}
                            className="p-1.5 text-white/30 hover:text-red-400 transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Applications Tab */}
          {activeTab === 'applications' && (
            <div>
              <div className="flex gap-2 mb-6">
                <button className="flex items-center gap-2 px-4 py-2 border text-sm border-white/30 bg-white/5 text-white">
                  <Mic className="w-4 h-4" />Podcasts <span className="px-1.5 py-0.5 bg-white/10 text-xs">{podcastApps.length}</span>
                </button>
              </div>
              
              <div className="space-y-3">
                {podcastApps.map(app => renderApplicationCard(app))}
                {podcastApps.length === 0 && (
                  <div className="text-center py-16 border border-white/10"><p className="text-white/40">No applications yet</p></div>
                )}
              </div>
            </div>
          )}

          {/* Podcast Series Tab */}
          {activeTab === 'podcastSeries' && (
            <div>
              <div className="flex justify-between items-center mb-6">
                <p className="text-white/50 text-sm">{podcastSeries.length} series</p>
                <button 
                  onClick={() => { setShowSeriesForm(true); setEditingSeries(null); setSeriesForm({ title: '', description: '', thumbnail_url: '' }); }} 
                  className="flex items-center gap-2 px-4 py-2 bg-white text-black text-sm font-medium"
                >
                  <Plus className="w-4 h-4" />Add Series
                </button>
              </div>

              {/* Series Form */}
              {showSeriesForm && (
                <div className="border border-white/10 p-6 mb-6 bg-white/[0.02]">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-white font-medium">{editingSeries ? 'Edit Series' : 'Add New Series'}</h3>
                    <button onClick={handleCancelSeriesForm} className="text-white/50 hover:text-white"><X className="w-5 h-5" /></button>
                  </div>
                  <div className="space-y-4">
                    <input
                      placeholder="Series Title"
                      value={seriesForm.title}
                      onChange={e => setSeriesForm({...seriesForm, title: e.target.value})}
                      className="w-full bg-white/[0.03] border border-white/10 px-4 py-3 text-white text-sm placeholder:text-white/30 focus:outline-none"
                    />
                    <textarea
                      placeholder="Description (optional)"
                      value={seriesForm.description}
                      onChange={e => setSeriesForm({...seriesForm, description: e.target.value})}
                      rows={2}
                      className="w-full bg-white/[0.03] border border-white/10 px-4 py-3 text-white text-sm placeholder:text-white/30 focus:outline-none resize-none"
                    />
                    <input
                      placeholder="Thumbnail URL (optional)"
                      value={seriesForm.thumbnail_url}
                      onChange={e => setSeriesForm({...seriesForm, thumbnail_url: e.target.value})}
                      className="w-full bg-white/[0.03] border border-white/10 px-4 py-3 text-white text-sm placeholder:text-white/30 focus:outline-none"
                    />
                  </div>
                  <div className="flex gap-3 mt-4">
                    <button onClick={handleSaveSeries} className="px-6 py-2 bg-white text-black text-sm font-medium flex items-center gap-2">
                      <Save className="w-4 h-4" />{editingSeries ? 'Update' : 'Save'}
                    </button>
                    <button onClick={handleCancelSeriesForm} className="px-6 py-2 border border-white/20 text-white/60 text-sm">Cancel</button>
                  </div>
                </div>
              )}

              {/* Episode Form Modal */}
              {showEpisodeForm && (
                <div className="border border-white/10 p-6 mb-6 bg-white/[0.02]">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-white font-medium">{editingEpisode ? 'Edit Episode' : 'Add Episode'}</h3>
                    <button onClick={handleCancelEpisodeForm} className="text-white/50 hover:text-white"><X className="w-5 h-5" /></button>
                  </div>
                  
                  {/* Episode Title */}
                  <input
                    placeholder="Episode Title"
                    value={episodeForm.title}
                    onChange={e => setEpisodeForm({...episodeForm, title: e.target.value})}
                    className="w-full bg-white/[0.03] border border-white/10 px-4 py-3 text-white text-sm placeholder:text-white/30 focus:outline-none mb-4"
                  />
                  
                  {/* Video Upload */}
                  <div className="border-2 border-dashed border-white/20 p-6 text-center mb-4">
                    <input
                      ref={episodeVideoInputRef}
                      type="file"
                      accept="video/*"
                      onChange={e => handleEpisodeVideoUpload(e.target.files[0])}
                      className="hidden"
                    />
                    {episodeUploading ? (
                      <div className="flex flex-col items-center gap-2">
                        <Loader2 className="w-8 h-8 text-white/60 animate-spin" />
                        <p className="text-white/60 text-sm">Uploading video...</p>
                      </div>
                    ) : episodeForm.video_url ? (
                      <div className="flex flex-col items-center gap-2">
                        <Video className="w-8 h-8 text-green-500" />
                        <p className="text-green-500 text-sm">Video uploaded!</p>
                        <button
                          type="button"
                          onClick={() => episodeVideoInputRef.current?.click()}
                          className="text-white/60 text-xs hover:text-white underline mt-2"
                        >
                          Replace video
                        </button>
                      </div>
                    ) : (
                      <div 
                        onClick={() => episodeVideoInputRef.current?.click()}
                        className="cursor-pointer"
                      >
                        <Upload className="w-8 h-8 text-white/40 mx-auto mb-2" />
                        <p className="text-white/60 text-sm">Click to upload video</p>
                        <p className="text-white/30 text-xs mt-1">MP4, MOV, WebM supported</p>
                      </div>
                    )}
                  </div>
                  
                  {/* Thumbnail preview */}
                  {episodeForm.thumbnail_url && (
                    <div className="mb-4">
                      <p className="text-white/50 text-xs mb-2">Auto-generated thumbnail</p>
                      <img src={episodeForm.thumbnail_url} alt="Thumbnail" className="w-24 h-32 object-cover border border-white/10" />
                    </div>
                  )}
                  
                  <div className="flex gap-3">
                    <button 
                      onClick={handleSaveEpisode} 
                      disabled={episodeUploading || !episodeForm.video_url}
                      className="px-6 py-2 bg-white text-black text-sm font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Save className="w-4 h-4" />{editingEpisode ? 'Update' : 'Save'}
                    </button>
                    <button onClick={handleCancelEpisodeForm} className="px-6 py-2 border border-white/20 text-white/60 text-sm">Cancel</button>
                  </div>
                </div>
              )}

              {/* Series List */}
              <div className="space-y-4">
                {podcastSeries.map(series => (
                  <div key={series.id} className="border border-white/10 bg-white/[0.02]">
                    {/* Series Header */}
                    <div className="p-4 flex items-center justify-between">
                      <button 
                        onClick={() => setExpandedSeriesId(expandedSeriesId === series.id ? null : series.id)}
                        className="flex-1 flex items-center gap-4 text-left"
                      >
                        {series.thumbnail_url ? (
                          <img src={series.thumbnail_url} alt={series.title} className="w-16 h-16 object-cover" />
                        ) : (
                          <div className="w-16 h-16 bg-white/5 flex items-center justify-center">
                            <Mic className="w-6 h-6 text-white/20" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <h4 className="text-white font-medium text-sm mb-1">{series.title}</h4>
                          {series.description && (
                            <p className="text-white/40 text-xs truncate">{series.description}</p>
                          )}
                          <p className="text-white/30 text-xs mt-1">
                            <PlayCircle className="w-3 h-3 inline mr-1" />
                            {series.episodes?.length || 0} episodes
                          </p>
                        </div>
                        {expandedSeriesId === series.id ? (
                          <ChevronUp className="w-5 h-5 text-white/40" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-white/40" />
                        )}
                      </button>
                      <div className="flex items-center gap-2 ml-4">
                        <button onClick={() => handleEditSeries(series)} className="p-2 text-white/40 hover:text-white">
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleDeleteSeries(series.id)} className="p-2 text-white/40 hover:text-red-400">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    
                    {/* Episodes List (Expanded) */}
                    {expandedSeriesId === series.id && (
                      <div className="border-t border-white/5 p-4 bg-white/[0.01]">
                        <div className="flex justify-between items-center mb-4">
                          <p className="text-white/40 text-xs uppercase tracking-wider">Episodes</p>
                          <button 
                            onClick={() => openAddEpisode(series.id)}
                            className="flex items-center gap-1 px-3 py-1 bg-white/10 text-white text-xs hover:bg-white/20"
                          >
                            <Plus className="w-3 h-3" />Add Episode
                          </button>
                        </div>
                        
                        {series.episodes && series.episodes.length > 0 ? (
                          <div className="space-y-2">
                            {series.episodes.map((episode, idx) => (
                              <div key={episode.id} className="flex items-center gap-3 p-3 bg-white/[0.02] border border-white/5">
                                <span className="text-white/30 text-xs w-6">{idx + 1}</span>
                                <div className="flex-1 min-w-0">
                                  <p className="text-white text-sm truncate">{episode.title}</p>
                                  <p className="text-white/30 text-xs truncate">{episode.dropbox_url}</p>
                                </div>
                                <div className="flex items-center gap-1">
                                  <button 
                                    onClick={() => { setActiveSeriesForEpisode(series.id); handleEditEpisode(episode); }}
                                    className="p-1.5 text-white/40 hover:text-white"
                                  >
                                    <Edit2 className="w-3 h-3" />
                                  </button>
                                  <button 
                                    onClick={() => handleDeleteEpisode(episode.id)}
                                    className="p-1.5 text-white/40 hover:text-red-400"
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-center py-8 text-white/30 text-sm">
                            No episodes yet. Add your first episode!
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
                
                {podcastSeries.length === 0 && (
                  <div className="text-center py-16 border border-white/10">
                    <Mic className="w-12 h-12 text-white/10 mx-auto mb-4" />
                    <p className="text-white/40">No podcast series yet</p>
                    <p className="text-white/30 text-sm mt-1">Create your first series to start adding episodes</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Videos Tab */}
          {activeTab === 'videos' && (
            <div>
              <div className="flex justify-between items-center mb-6">
                <p className="text-white/50 text-sm">{videos.length} videos</p>
                <button 
                  onClick={() => { setShowVideoForm(true); setEditingVideo(null); setVideoForm({ title: '', dropbox_url: '', thumbnail_url: '', tags: [] }); }} 
                  className="flex items-center gap-2 px-4 py-2 bg-white text-black text-sm font-medium"
                >
                  <Plus className="w-4 h-4" />Add Video
                </button>
              </div>

              {/* Video Form */}
              {showVideoForm && (
                <div className="border border-white/10 p-6 mb-6 bg-white/[0.02]">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-white font-medium">{editingVideo ? 'Edit Video' : 'Add New Video'}</h3>
                    <button onClick={handleCancelVideoForm} className="text-white/50 hover:text-white"><X className="w-5 h-5" /></button>
                  </div>
                  <div className="space-y-4 mb-4">
                    <input
                      placeholder="Video Title"
                      value={videoForm.title}
                      onChange={e => setVideoForm({...videoForm, title: e.target.value})}
                      className="w-full bg-white/[0.03] border border-white/10 px-4 py-3 text-white text-sm placeholder:text-white/30 focus:outline-none"
                    />
                    
                    {/* Video Upload */}
                    <div className="border-2 border-dashed border-white/20 p-6 text-center">
                      <input
                        ref={videoInputRef}
                        type="file"
                        accept="video/*"
                        onChange={e => handleVideoUpload(e.target.files[0])}
                        className="hidden"
                      />
                      {uploading ? (
                        <div className="flex flex-col items-center gap-2">
                          <Loader2 className="w-8 h-8 text-white/60 animate-spin" />
                          <p className="text-white/60 text-sm">{uploadProgress}</p>
                        </div>
                      ) : videoForm.video_url ? (
                        <div className="flex flex-col items-center gap-2">
                          <Video className="w-8 h-8 text-green-500" />
                          <p className="text-green-500 text-sm">Video uploaded!</p>
                          <p className="text-white/40 text-xs truncate max-w-full">{videoForm.video_url}</p>
                          <button
                            type="button"
                            onClick={() => videoInputRef.current?.click()}
                            className="text-white/60 text-xs hover:text-white underline mt-2"
                          >
                            Replace video
                          </button>
                        </div>
                      ) : (
                        <div 
                          onClick={() => videoInputRef.current?.click()}
                          className="cursor-pointer"
                        >
                          <Upload className="w-8 h-8 text-white/40 mx-auto mb-2" />
                          <p className="text-white/60 text-sm">Click to upload video</p>
                          <p className="text-white/30 text-xs mt-1">MP4, MOV, WebM supported</p>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Thumbnail (auto-generated but can override) */}
                  {videoForm.thumbnail_url && (
                    <div className="mb-4">
                      <p className="text-white/50 text-xs mb-2">Auto-generated thumbnail</p>
                      <img src={videoForm.thumbnail_url} alt="Thumbnail" className="w-32 h-20 object-cover border border-white/10" />
                    </div>
                  )}
                  
                  <div className="mb-4">
                    <p className="text-white/50 text-xs mb-2">Tags</p>
                    <div className="flex flex-wrap gap-2">
                      {tags.map(tag => (
                        <button
                          key={tag.id}
                          type="button"
                          onClick={() => {
                            const newTags = videoForm.tags.includes(tag.slug) 
                              ? videoForm.tags.filter(t => t !== tag.slug)
                              : [...videoForm.tags, tag.slug];
                            setVideoForm({...videoForm, tags: newTags});
                          }}
                          className={`px-3 py-1 text-xs border transition-all ${
                            videoForm.tags.includes(tag.slug) ? 'border-white bg-white text-black' : 'border-white/20 text-white/60 hover:border-white/40'
                          }`}
                        >
                          {tag.name}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <button 
                      onClick={handleSaveVideo} 
                      disabled={uploading || !videoForm.video_url}
                      className="px-6 py-2 bg-white text-black text-sm font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Save className="w-4 h-4" />{editingVideo ? 'Update' : 'Save'}
                    </button>
                    <button onClick={handleCancelVideoForm} className="px-6 py-2 border border-white/20 text-white/60 text-sm">Cancel</button>
                  </div>
                </div>
              )}

              {/* Video List */}
              <div className="space-y-3">
                {videos.map(video => (
                  <div key={video.id} className="border border-white/10 p-4 bg-white/[0.02] flex items-center justify-between">
                    <div className="flex items-center gap-4 flex-1 min-w-0">
                      {video.thumbnail_url && (
                        <img src={video.thumbnail_url} alt={video.title} className="w-20 h-12 object-cover flex-shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <h4 className="text-white font-medium text-sm mb-1 truncate">{video.title}</h4>
                        <div className="flex flex-wrap gap-1">
                        {video.tags.map(tag => (
                          <span key={tag} className="px-2 py-0.5 bg-white/10 text-white/60 text-xs capitalize">{tag}</span>
                        ))}
                      </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      <button onClick={() => handleEditVideo(video)} className="p-2 text-white/40 hover:text-white">
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleDeleteVideo(video.id)} className="p-2 text-white/40 hover:text-red-400">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
                {videos.length === 0 && (
                  <div className="text-center py-16 border border-white/10"><p className="text-white/40">No videos yet</p></div>
                )}
              </div>
            </div>
          )}

          {/* Tags Tab */}
          {activeTab === 'tags' && (
            <div>
              <div className="flex justify-between items-center mb-6">
                <p className="text-white/50 text-sm">{tags.length} tags</p>
                <button onClick={() => setShowTagForm(true)} className="flex items-center gap-2 px-4 py-2 bg-white text-black text-sm font-medium">
                  <Plus className="w-4 h-4" />Add Tag
                </button>
              </div>

              {showTagForm && (
                <div className="border border-white/10 p-6 mb-6 bg-white/[0.02] flex gap-3">
                  <input
                    placeholder="Tag name"
                    value={newTag}
                    onChange={e => setNewTag(e.target.value)}
                    onKeyPress={e => e.key === 'Enter' && handleAddTag()}
                    className="flex-1 bg-white/[0.03] border border-white/10 px-4 py-3 text-white text-sm placeholder:text-white/30 focus:outline-none"
                  />
                  <button onClick={handleAddTag} className="px-6 py-2 bg-white text-black text-sm font-medium">Add</button>
                  <button onClick={() => { setShowTagForm(false); setNewTag(''); }} className="px-4 py-2 border border-white/10 text-white/50 text-sm">Cancel</button>
                </div>
              )}

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {tags.map(tag => (
                  <div key={tag.id} className="border border-white/10 p-4 bg-white/[0.02] flex items-center justify-between">
                    <span className="text-white text-sm">{tag.name}</span>
                    <button onClick={() => handleDeleteTag(tag.id)} className="p-1 text-white/40 hover:text-red-400">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Stories Tab */}
          {activeTab === 'stories' && (
            <div>
              <div className="flex justify-between items-center mb-6">
                <p className="text-white/50 text-sm">{stories.length} stories</p>
                <button 
                  onClick={() => { setShowStoryForm(true); setEditingStory(null); }} 
                  className="flex items-center gap-2 px-4 py-2 bg-white text-black text-sm font-medium"
                >
                  <Plus className="w-4 h-4" />Add Story
                </button>
              </div>

              {/* Story Form */}
              {showStoryForm && (
                <div className="border border-white/10 p-6 mb-6 bg-white/[0.02]">
                  <div className="flex justify-between items-center mb-6">
                    <h3 className="text-white font-medium text-lg">{editingStory ? 'Edit Story' : 'Add New Story'}</h3>
                    <button onClick={handleCancelStoryForm} className="text-white/50 hover:text-white"><X className="w-5 h-5" /></button>
                  </div>
                  
                  <div className="space-y-6">
                    {/* Basic Info */}
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-white/50 text-xs uppercase mb-2 block">Client Name *</label>
                        <input
                          placeholder="e.g., IVRY Beauty Salon"
                          value={storyForm.client_name}
                          onChange={e => setStoryForm({...storyForm, client_name: e.target.value})}
                          className="w-full bg-white/[0.03] border border-white/10 px-4 py-3 text-white text-sm placeholder:text-white/30 focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="text-white/50 text-xs uppercase mb-2 block">Story Title *</label>
                        <input
                          placeholder="e.g., From Vision to Reality: The IVRY Story"
                          value={storyForm.title}
                          onChange={e => setStoryForm({...storyForm, title: e.target.value})}
                          className="w-full bg-white/[0.03] border border-white/10 px-4 py-3 text-white text-sm placeholder:text-white/30 focus:outline-none"
                        />
                      </div>
                    </div>
                    
                    {/* URL Slug */}
                    <div>
                      <label className="text-white/50 text-xs uppercase mb-2 block">URL Slug (auto-generated if empty)</label>
                      <input
                        placeholder="e.g., ivry-beauty-salon"
                        value={storyForm.slug}
                        onChange={e => setStoryForm({...storyForm, slug: e.target.value})}
                        className="w-full bg-white/[0.03] border border-white/10 px-4 py-3 text-white text-sm placeholder:text-white/30 focus:outline-none"
                        disabled={editingStory}
                      />
                      {editingStory && <p className="text-white/30 text-xs mt-1">Slug cannot be changed after creation</p>}
                    </div>
                    
                    {/* Hero Media */}
                    <div>
                      <label className="text-white/50 text-xs uppercase mb-3 block">Hero Media</label>
                      <div className="grid md:grid-cols-2 gap-4">
                        {/* Hero Image Upload */}
                        <div className="border border-dashed border-white/20 p-4 text-center">
                          <input
                            ref={storyHeroImageRef}
                            type="file"
                            accept="image/*"
                            onChange={e => handleStoryHeroImageUpload(e.target.files[0])}
                            className="hidden"
                          />
                          {storyUploading === 'hero_image' ? (
                            <div className="flex flex-col items-center gap-2 py-4">
                              <Loader2 className="w-6 h-6 text-white/60 animate-spin" />
                              <p className="text-white/60 text-xs">Uploading...</p>
                            </div>
                          ) : storyForm.hero_image ? (
                            <div className="flex flex-col items-center gap-2">
                              <img src={storyForm.hero_image} alt="Hero" className="w-full h-32 object-cover mb-2" />
                              <button onClick={() => storyHeroImageRef.current?.click()} className="text-white/60 text-xs hover:text-white underline">Replace image</button>
                              <button onClick={() => setStoryForm(prev => ({...prev, hero_image: ''}))} className="text-red-400/60 text-xs hover:text-red-400">Remove</button>
                            </div>
                          ) : (
                            <div onClick={() => storyHeroImageRef.current?.click()} className="cursor-pointer py-4">
                              <Image className="w-8 h-8 text-white/40 mx-auto mb-2" />
                              <p className="text-white/60 text-xs">Upload Hero Image</p>
                            </div>
                          )}
                        </div>
                        
                        {/* Hero Video Upload */}
                        <div className="border border-dashed border-white/20 p-4 text-center">
                          <input
                            ref={storyHeroVideoRef}
                            type="file"
                            accept="video/*"
                            onChange={e => handleStoryHeroVideoUpload(e.target.files[0])}
                            className="hidden"
                          />
                          {storyUploading === 'hero_video' ? (
                            <div className="flex flex-col items-center gap-2 py-4">
                              <Loader2 className="w-6 h-6 text-white/60 animate-spin" />
                              <p className="text-white/60 text-xs">Uploading video...</p>
                            </div>
                          ) : storyForm.hero_video ? (
                            <div className="flex flex-col items-center gap-2">
                              <Video className="w-8 h-8 text-green-500 mb-2" />
                              <p className="text-green-500 text-xs">Hero video uploaded</p>
                              <button onClick={() => storyHeroVideoRef.current?.click()} className="text-white/60 text-xs hover:text-white underline">Replace video</button>
                              <button onClick={() => setStoryForm(prev => ({...prev, hero_video: ''}))} className="text-red-400/60 text-xs hover:text-red-400">Remove</button>
                            </div>
                          ) : (
                            <div onClick={() => storyHeroVideoRef.current?.click()} className="cursor-pointer py-4">
                              <Video className="w-8 h-8 text-white/40 mx-auto mb-2" />
                              <p className="text-white/60 text-xs">Upload Hero Video (optional)</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Story Content - Block Editor */}
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <label className="text-white/50 text-xs uppercase">Story Content Blocks</label>
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => addContentBlock('text')}
                            className="px-3 py-1.5 bg-white/10 text-white text-xs hover:bg-white/20 transition-colors"
                          >
                            + Text
                          </button>
                          <button
                            type="button"
                            onClick={() => addContentBlock('image')}
                            className="px-3 py-1.5 bg-white/10 text-white text-xs hover:bg-white/20 transition-colors"
                          >
                            + Image
                          </button>
                          <button
                            type="button"
                            onClick={() => addContentBlock('video')}
                            className="px-3 py-1.5 bg-white/10 text-white text-xs hover:bg-white/20 transition-colors"
                          >
                            + Video
                          </button>
                          <button
                            type="button"
                            onClick={() => addContentBlock('youtube')}
                            className="px-3 py-1.5 bg-white/10 text-white text-xs hover:bg-white/20 transition-colors"
                          >
                            + YouTube
                          </button>
                          <button
                            type="button"
                            onClick={() => addContentBlock('quote')}
                            className="px-3 py-1.5 bg-white/10 text-white text-xs hover:bg-white/20 transition-colors"
                          >
                            + Quote
                          </button>
                        </div>
                      </div>
                      
                      {/* Content Blocks List */}
                      <div className="space-y-4">
                        {storyForm.content_blocks.length === 0 ? (
                          <div className="border border-dashed border-white/20 p-8 text-center">
                            <p className="text-white/40 text-sm mb-2">No content blocks yet</p>
                            <p className="text-white/30 text-xs">Click the buttons above to add text, images, videos, or quotes</p>
                          </div>
                        ) : (
                          storyForm.content_blocks.map((block, idx) => (
                            <div key={block.id} className="border border-white/10 bg-white/[0.02] p-4">
                              <div className="flex items-center justify-between mb-3">
                                <span className="text-white/50 text-xs uppercase flex items-center gap-2">
                                  <span className="w-5 h-5 rounded-full bg-white/10 flex items-center justify-center text-[10px]">{idx + 1}</span>
                                  {block.type === 'text' && 'Text Block'}
                                  {block.type === 'image' && 'Image'}
                                  {block.type === 'gif' && 'GIF'}
                                  {block.type === 'video' && 'Video'}
                                  {block.type === 'youtube' && 'YouTube'}
                                  {block.type === 'quote' && 'Quote'}
                                </span>
                                <div className="flex items-center gap-1">
                                  <button
                                    type="button"
                                    onClick={() => moveContentBlock(block.id, 'up')}
                                    disabled={idx === 0}
                                    className="p-1 text-white/40 hover:text-white disabled:opacity-30"
                                  >
                                    ↑
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => moveContentBlock(block.id, 'down')}
                                    disabled={idx === storyForm.content_blocks.length - 1}
                                    className="p-1 text-white/40 hover:text-white disabled:opacity-30"
                                  >
                                    ↓
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => deleteContentBlock(block.id)}
                                    className="p-1 text-white/40 hover:text-red-400 ml-2"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              </div>
                              
                              {/* Text Block */}
                              {block.type === 'text' && (
                                <textarea
                                  placeholder="Write your paragraph here..."
                                  value={block.content}
                                  onChange={e => updateContentBlock(block.id, 'content', e.target.value)}
                                  rows={4}
                                  className="w-full bg-white/[0.03] border border-white/10 px-4 py-3 text-white text-sm placeholder:text-white/30 focus:outline-none resize-y"
                                />
                              )}
                              
                              {/* Image/GIF Block */}
                              {(block.type === 'image' || block.type === 'gif') && (
                                <div>
                                  {block.content ? (
                                    <div className="relative">
                                      <img src={block.content} alt="Content" className="w-full max-h-64 object-contain bg-black/20" />
                                      <button
                                        type="button"
                                        onClick={() => updateContentBlock(block.id, 'content', '')}
                                        className="absolute top-2 right-2 p-1 bg-red-500/80 text-white rounded"
                                      >
                                        <X className="w-4 h-4" />
                                      </button>
                                    </div>
                                  ) : (
                                    <div className="flex gap-2">
                                      <input
                                        type="text"
                                        placeholder="Paste image/GIF URL or upload..."
                                        value={block.content}
                                        onChange={e => updateContentBlock(block.id, 'content', e.target.value)}
                                        className="flex-1 bg-white/[0.03] border border-white/10 px-4 py-2 text-white text-sm placeholder:text-white/30 focus:outline-none"
                                      />
                                      <label className="px-4 py-2 bg-white/10 text-white text-sm cursor-pointer hover:bg-white/20 transition-colors">
                                        {storyUploading === `block_${block.id}` ? 'Uploading...' : 'Upload'}
                                        <input
                                          type="file"
                                          accept="image/*"
                                          className="hidden"
                                          onChange={e => handleBlockMediaUpload(block.id, e.target.files[0], 'image')}
                                        />
                                      </label>
                                    </div>
                                  )}
                                  <input
                                    type="text"
                                    placeholder="Caption (optional)"
                                    value={block.caption || ''}
                                    onChange={e => updateContentBlock(block.id, 'caption', e.target.value)}
                                    className="w-full mt-2 bg-white/[0.03] border border-white/10 px-4 py-2 text-white text-sm placeholder:text-white/30 focus:outline-none"
                                  />
                                </div>
                              )}
                              
                              {/* Video Block */}
                              {block.type === 'video' && (
                                <div>
                                  {block.content ? (
                                    <div className="relative">
                                      <video src={block.content} controls className="w-full max-h-64" />
                                      <button
                                        type="button"
                                        onClick={() => updateContentBlock(block.id, 'content', '')}
                                        className="absolute top-2 right-2 p-1 bg-red-500/80 text-white rounded"
                                      >
                                        <X className="w-4 h-4" />
                                      </button>
                                    </div>
                                  ) : (
                                    <div className="flex gap-2">
                                      <input
                                        type="text"
                                        placeholder="Paste video URL or upload..."
                                        value={block.content}
                                        onChange={e => updateContentBlock(block.id, 'content', e.target.value)}
                                        className="flex-1 bg-white/[0.03] border border-white/10 px-4 py-2 text-white text-sm placeholder:text-white/30 focus:outline-none"
                                      />
                                      <label className="px-4 py-2 bg-white/10 text-white text-sm cursor-pointer hover:bg-white/20 transition-colors">
                                        {storyUploading === `block_${block.id}` ? 'Uploading...' : 'Upload'}
                                        <input
                                          type="file"
                                          accept="video/*"
                                          className="hidden"
                                          onChange={e => handleBlockMediaUpload(block.id, e.target.files[0], 'video')}
                                        />
                                      </label>
                                    </div>
                                  )}
                                  <input
                                    type="text"
                                    placeholder="Caption (optional)"
                                    value={block.caption || ''}
                                    onChange={e => updateContentBlock(block.id, 'caption', e.target.value)}
                                    className="w-full mt-2 bg-white/[0.03] border border-white/10 px-4 py-2 text-white text-sm placeholder:text-white/30 focus:outline-none"
                                  />
                                </div>
                              )}
                              
                              {/* YouTube Block */}
                              {block.type === 'youtube' && (
                                <div>
                                  <input
                                    type="text"
                                    placeholder="Paste YouTube URL (e.g., https://youtube.com/watch?v=...)"
                                    value={block.content}
                                    onChange={e => updateContentBlock(block.id, 'content', e.target.value)}
                                    className="w-full bg-white/[0.03] border border-white/10 px-4 py-2 text-white text-sm placeholder:text-white/30 focus:outline-none"
                                  />
                                  <input
                                    type="text"
                                    placeholder="Caption (optional)"
                                    value={block.caption || ''}
                                    onChange={e => updateContentBlock(block.id, 'caption', e.target.value)}
                                    className="w-full mt-2 bg-white/[0.03] border border-white/10 px-4 py-2 text-white text-sm placeholder:text-white/30 focus:outline-none"
                                  />
                                  {/* Preview */}
                                  {block.content && block.content.includes('youtube') && (
                                    <div className="mt-2 aspect-video bg-black/20 flex items-center justify-center">
                                      <span className="text-white/40 text-sm">YouTube preview will show on story page</span>
                                    </div>
                                  )}
                                </div>
                              )}
                              
                              {/* Quote Block */}
                              {block.type === 'quote' && (
                                <div>
                                  <textarea
                                    placeholder="Enter the quote text..."
                                    value={block.content}
                                    onChange={e => updateContentBlock(block.id, 'content', e.target.value)}
                                    rows={3}
                                    className="w-full bg-white/[0.03] border border-white/10 px-4 py-3 text-white text-sm italic placeholder:text-white/30 focus:outline-none resize-y"
                                  />
                                  <input
                                    type="text"
                                    placeholder="Attribution (e.g., John Doe, CEO)"
                                    value={block.caption || ''}
                                    onChange={e => updateContentBlock(block.id, 'caption', e.target.value)}
                                    className="w-full mt-2 bg-white/[0.03] border border-white/10 px-4 py-2 text-white text-sm placeholder:text-white/30 focus:outline-none"
                                  />
                                </div>
                              )}
                            </div>
                          ))
                        )}
                      </div>
                      
                      {/* Legacy Content (hidden but still saved) */}
                      {storyForm.content && storyForm.content_blocks.length === 0 && (
                        <div className="mt-4 p-4 border border-yellow-500/30 bg-yellow-500/5">
                          <p className="text-yellow-500/70 text-xs mb-2">Legacy content detected (plain text format)</p>
                          <p className="text-white/50 text-xs">This story uses the old plain text format. You can continue using it or convert to blocks for richer layouts.</p>
                          <button
                            type="button"
                            onClick={() => {
                              // Convert legacy content to text blocks
                              const paragraphs = storyForm.content.split('\n\n').filter(p => p.trim());
                              const blocks = paragraphs.map((p, idx) => ({
                                id: Date.now().toString() + idx,
                                type: 'text',
                                content: p.trim(),
                                caption: '',
                                order: idx
                              }));
                              setStoryForm(prev => ({ ...prev, content_blocks: blocks, content: '' }));
                              toast.success('Converted to content blocks!');
                            }}
                            className="mt-2 px-3 py-1 bg-yellow-500/20 text-yellow-500 text-xs hover:bg-yellow-500/30 transition-colors"
                          >
                            Convert to Blocks
                          </button>
                        </div>
                      )}
                    </div>
                    
                    {/* Services Provided - Dropdown */}
                    <div>
                      <label className="text-white/50 text-xs uppercase mb-2 block">Services Provided</label>
                      <div className="flex flex-wrap gap-2 mb-3">
                        {storyForm.services_provided.map((service, idx) => (
                          <span key={idx} className="px-3 py-1 bg-white/10 text-white text-xs flex items-center gap-2">
                            {service}
                            <button onClick={() => handleRemoveService(service)} className="text-white/40 hover:text-red-400">
                              <X className="w-3 h-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                      
                      {/* Service Dropdown */}
                      <select
                        value=""
                        onChange={e => {
                          if (e.target.value) {
                            handleAddService(e.target.value);
                          }
                        }}
                        className="w-full bg-white/[0.03] border border-white/10 px-4 py-3 text-white text-sm focus:outline-none cursor-pointer"
                      >
                        <option value="" className="bg-[#0a0a0a]">Select a service to add...</option>
                        <optgroup label="Production" className="bg-[#0a0a0a]">
                          <option value="Film Production" className="bg-[#0a0a0a]">Film Production</option>
                          <option value="Wedding Cinematography" className="bg-[#0a0a0a]">Wedding Cinematography</option>
                          <option value="Real Estate Media" className="bg-[#0a0a0a]">Real Estate Media</option>
                          <option value="Event Broadcasting" className="bg-[#0a0a0a]">Event Broadcasting</option>
                        </optgroup>
                        <optgroup label="Digital" className="bg-[#0a0a0a]">
                          <option value="Website Development" className="bg-[#0a0a0a]">Website Development</option>
                          <option value="SEO Strategies" className="bg-[#0a0a0a]">SEO Strategies</option>
                          <option value="Digital Advertising" className="bg-[#0a0a0a]">Digital Advertising</option>
                          <option value="AI Automations" className="bg-[#0a0a0a]">AI Automations</option>
                        </optgroup>
                        <optgroup label="Creative" className="bg-[#0a0a0a]">
                          <option value="Brand Identity" className="bg-[#0a0a0a]">Brand Identity</option>
                          <option value="Content Creation" className="bg-[#0a0a0a]">Content Creation</option>
                          <option value="Graphic Design" className="bg-[#0a0a0a]">Graphic Design</option>
                          <option value="Motion Graphics" className="bg-[#0a0a0a]">Motion Graphics</option>
                        </optgroup>
                        <optgroup label="Consulting" className="bg-[#0a0a0a]">
                          <option value="Media Strategy" className="bg-[#0a0a0a]">Media Strategy</option>
                          <option value="Technology Advisory" className="bg-[#0a0a0a]">Technology Advisory</option>
                          <option value="Budget & Appraisals" className="bg-[#0a0a0a]">Budget &amp; Appraisals</option>
                          <option value="Automation Consulting" className="bg-[#0a0a0a]">Automation Consulting</option>
                          <option value="Content Strategy" className="bg-[#0a0a0a]">Content Strategy</option>
                          <option value="Audience Audit" className="bg-[#0a0a0a]">Audience Audit</option>
                        </optgroup>
                      </select>
                      
                      {/* Custom Service Input */}
                      <div className="flex gap-2 mt-2">
                        <input
                          id="newServiceInput"
                          placeholder="Or add a custom service..."
                          className="flex-1 bg-white/[0.03] border border-white/10 px-4 py-2 text-white text-sm placeholder:text-white/30 focus:outline-none"
                          onKeyPress={e => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleAddService(e.target.value);
                              e.target.value = '';
                            }
                          }}
                        />
                        <button 
                          onClick={() => {
                            const input = document.getElementById('newServiceInput');
                            if (input.value) {
                              handleAddService(input.value);
                              input.value = '';
                            }
                          }}
                          className="px-4 py-2 bg-white/10 text-white text-sm hover:bg-white/20 transition-colors"
                        >
                          Add
                        </button>
                      </div>
                    </div>
                    
                    {/* Podcast Link */}
                    <div>
                      <label className="text-white/50 text-xs uppercase mb-2 block">Podcast Link (optional)</label>
                      <input
                        placeholder="https://example.com/podcast-episode"
                        value={storyForm.podcast_link}
                        onChange={e => setStoryForm({...storyForm, podcast_link: e.target.value})}
                        className="w-full bg-white/[0.03] border border-white/10 px-4 py-3 text-white text-sm placeholder:text-white/30 focus:outline-none"
                      />
                    </div>
                    
                    {/* Gallery Images */}
                    <div>
                      <label className="text-white/50 text-xs uppercase mb-3 block">Gallery Images</label>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                        {storyForm.gallery_images.map((img, idx) => (
                          <div key={idx} className="relative group">
                            <img src={img.url} alt={`Gallery ${idx + 1}`} className="w-full aspect-square object-cover" />
                            <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                              <button onClick={() => handleRemoveGalleryImage(idx)} className="p-2 bg-red-500/80 rounded-full">
                                <Trash2 className="w-4 h-4 text-white" />
                              </button>
                            </div>
                            <input
                              placeholder="Caption"
                              value={img.caption || ''}
                              onChange={e => handleUpdateGalleryCaption(idx, e.target.value)}
                              className="w-full mt-1 bg-white/[0.03] border border-white/10 px-2 py-1 text-white text-xs placeholder:text-white/30 focus:outline-none"
                            />
                          </div>
                        ))}
                        
                        {/* Add Gallery Image */}
                        <div 
                          onClick={() => !storyUploading && storyGalleryRef.current?.click()}
                          className="border border-dashed border-white/20 aspect-square flex items-center justify-center cursor-pointer hover:border-white/40 transition-colors"
                        >
                          <input
                            ref={storyGalleryRef}
                            type="file"
                            accept="image/*"
                            onChange={e => handleStoryGalleryImageUpload(e.target.files[0])}
                            className="hidden"
                          />
                          {storyUploading === 'gallery' ? (
                            <Loader2 className="w-6 h-6 text-white/60 animate-spin" />
                          ) : (
                            <Plus className="w-6 h-6 text-white/40" />
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Videos */}
                    <div>
                      <label className="text-white/50 text-xs uppercase mb-3 block">Videos</label>
                      <div className="space-y-3 mb-3">
                        {storyForm.videos.map((vid, idx) => (
                          <div key={idx} className="flex items-center gap-3 p-3 bg-white/[0.02] border border-white/10">
                            <Video className="w-5 h-5 text-white/40 flex-shrink-0" />
                            <input
                              placeholder="Video title"
                              value={vid.title || ''}
                              onChange={e => handleUpdateVideoTitle(idx, e.target.value)}
                              className="flex-1 bg-white/[0.03] border border-white/10 px-3 py-2 text-white text-sm placeholder:text-white/30 focus:outline-none"
                            />
                            <button onClick={() => handleRemoveStoryVideo(idx)} className="p-2 text-white/40 hover:text-red-400">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                      
                      {/* Add Video */}
                      <div 
                        onClick={() => !storyUploading && storyVideoRef.current?.click()}
                        className="border border-dashed border-white/20 p-4 text-center cursor-pointer hover:border-white/40 transition-colors"
                      >
                        <input
                          ref={storyVideoRef}
                          type="file"
                          accept="video/*"
                          onChange={e => handleStoryVideoUpload(e.target.files[0])}
                          className="hidden"
                        />
                        {storyUploading === 'video' ? (
                          <div className="flex items-center justify-center gap-2">
                            <Loader2 className="w-5 h-5 text-white/60 animate-spin" />
                            <span className="text-white/60 text-sm">Uploading video...</span>
                          </div>
                        ) : (
                          <div className="flex items-center justify-center gap-2">
                            <Upload className="w-5 h-5 text-white/40" />
                            <span className="text-white/60 text-sm">Add Video</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Form Actions */}
                  <div className="flex gap-3 mt-8 pt-6 border-t border-white/10">
                    <button 
                      onClick={handleSaveStory} 
                      disabled={storyUploading || !storyForm.client_name || !storyForm.title}
                      className="px-8 py-3 bg-white text-black text-sm font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Save className="w-4 h-4" />{editingStory ? 'Update Story' : 'Create Story'}
                    </button>
                    <button onClick={handleCancelStoryForm} className="px-8 py-3 border border-white/20 text-white/60 text-sm">Cancel</button>
                  </div>
                </div>
              )}

              {/* Stories List */}
              <div className="space-y-4">
                {stories.map(story => (
                  <div key={story.slug} className="border border-white/10 bg-white/[0.02]">
                    <div className="p-4 flex items-center justify-between">
                      <div className="flex items-center gap-4 flex-1 min-w-0">
                        {story.hero_image ? (
                          <img src={story.hero_image} alt={story.client_name} className="w-20 h-20 object-cover flex-shrink-0" />
                        ) : (
                          <div className="w-20 h-20 bg-white/5 flex items-center justify-center flex-shrink-0">
                            <BookOpen className="w-8 h-8 text-white/20" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <h4 className="text-white font-medium text-sm mb-1 truncate">{story.client_name}</h4>
                          <p className="text-white/50 text-xs truncate mb-2">{story.title}</p>
                          <div className="flex flex-wrap gap-1">
                            {story.services_provided?.slice(0, 3).map((service, idx) => (
                              <span key={idx} className="px-2 py-0.5 bg-white/10 text-white/60 text-xs">{service}</span>
                            ))}
                            {story.services_provided?.length > 3 && (
                              <span className="px-2 py-0.5 bg-white/5 text-white/40 text-xs">+{story.services_provided.length - 3} more</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 ml-4">
                        <a 
                          href={`/stories/${story.slug}`} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="p-2 text-white/40 hover:text-white"
                          title="View story"
                        >
                          <PlayCircle className="w-4 h-4" />
                        </a>
                        <button onClick={() => handleEditStory(story)} className="p-2 text-white/40 hover:text-white" title="Edit story">
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleDeleteStory(story.slug)} className="p-2 text-white/40 hover:text-red-400" title="Delete story">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
                
                {stories.length === 0 && (
                  <div className="text-center py-16 border border-white/10">
                    <BookOpen className="w-12 h-12 text-white/10 mx-auto mb-4" />
                    <p className="text-white/40">No stories yet</p>
                    <p className="text-white/30 text-sm mt-1">Create your first success story</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* API Access Tab */}
          {activeTab === 'apiAccess' && <ApiAccessTab authToken={authToken} />}

          {/* Security Tab */}
          {activeTab === 'security' && <SecurityTab authToken={authToken} />}

          {/* Settings Tab */}
          {activeTab === 'settings' && (
            <div className="max-w-2xl">
              <div className="border border-white/10 p-6 bg-white/[0.02]">
                <h3 className="text-white font-medium mb-6 flex items-center gap-2"><Image className="w-5 h-5" />About Page Portrait</h3>
                
                <div className="mb-4">
                  <label className="text-white/50 text-xs uppercase mb-2 block">Portrait Image URL</label>
                  <input
                    placeholder="https://example.com/portrait.jpg"
                    value={siteSettings.about_portrait_url || ''}
                    onChange={e => setSiteSettings({...siteSettings, about_portrait_url: e.target.value})}
                    className="w-full bg-white/[0.03] border border-white/10 px-4 py-3 text-white text-sm placeholder:text-white/30 focus:outline-none"
                  />
                </div>

                {siteSettings.about_portrait_url && (
                  <div className="mb-4">
                    <p className="text-white/50 text-xs uppercase mb-2">Preview</p>
                    <img src={siteSettings.about_portrait_url} alt="Portrait preview" className="w-32 h-40 object-cover border border-white/10" />
                  </div>
                )}

                <button onClick={handleUpdateSettings} className="px-6 py-2 bg-white text-black text-sm font-medium flex items-center gap-2">
                  <Check className="w-4 h-4" />Save Settings
                </button>
              </div>
            </div>
          )}

          {/* Service Images Tab */}
          {activeTab === 'serviceImages' && (
            <div>
              <div className="mb-6">
                <p className="text-white/50 text-sm">Upload custom hero images for each service page. Images will be optimized automatically.</p>
              </div>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {SERVICE_SLUGS.map(service => (
                  <div key={service.slug} className="border border-white/10 p-4 bg-white/[0.02]">
                    <h4 className="text-white font-medium text-sm mb-3">{service.name}</h4>
                    
                    {/* Current Image Preview */}
                    <div className="aspect-video bg-white/5 mb-3 overflow-hidden relative">
                      {serviceImages[service.slug] ? (
                        <img 
                          src={serviceImages[service.slug]} 
                          alt={service.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Image className="w-8 h-8 text-white/20" />
                        </div>
                      )}
                      
                      {uploadingService === service.slug && (
                        <div className="absolute inset-0 bg-black/70 flex items-center justify-center">
                          <Loader2 className="w-6 h-6 text-white animate-spin" />
                        </div>
                      )}
                    </div>
                    
                    {/* Upload Button */}
                    <input
                      type="file"
                      accept="image/*"
                      ref={el => serviceImageRefs.current[service.slug] = el}
                      onChange={e => handleServiceImageUpload(service.slug, e.target.files[0])}
                      className="hidden"
                    />
                    <button
                      onClick={() => serviceImageRefs.current[service.slug]?.click()}
                      disabled={uploadingService === service.slug}
                      className="w-full px-3 py-2 bg-white/10 text-white text-xs hover:bg-white/20 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                      <Upload className="w-3 h-3" />
                      {serviceImages[service.slug] ? 'Replace Image' : 'Upload Image'}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default AdminPage;
